import { useEffect, useRef, useState } from "react";
import DailyIframe, { DailyCall } from "@daily-co/daily-js";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, AlertCircle, Video, VideoOff } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface VideoCallRoomProps {
  roomUrl: string;
  onLeave?: () => void;
  onJoinError?: (error: any) => void;
  userName: string;
}

export const VideoCallRoom = ({ roomUrl, onLeave, onJoinError, userName }: VideoCallRoomProps) => {
  const callContainerRef = useRef<HTMLDivElement>(null);
  const callObjectRef = useRef<DailyCall | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInCall, setIsInCall] = useState(false);

  useEffect(() => {
    const container = callContainerRef.current;
    if (!roomUrl || !container) return;

    const initializeCall = async () => {
      try {
        console.log("Initializing Daily.co call for room:", roomUrl);
        
        // Wait a tick to ensure iframe container is attached
        await new Promise((r) => setTimeout(r, 0));
        
        // Create Daily call object - use the container we stored
        const callObject = DailyIframe.createFrame(container, {
          iframeStyle: {
            width: "100%",
            height: "100%",
            border: "0",
            borderRadius: "8px",
          },
          showLeaveButton: true,
          showFullscreenButton: true,
          theme: {
            colors: {
              accent: "#ef4444",
              accentText: "#ffffff",
              background: "#0f1419",
              backgroundAccent: "#1a202c",
              baseText: "#ffffff",
              border: "#2d3748",
              mainAreaBg: "#0f1419",
              mainAreaBgAccent: "#1a202c",
              mainAreaText: "#ffffff",
              supportiveText: "#a0aec0",
            },
          },
        });

        callObjectRef.current = callObject;

        // Set up event listeners
        callObject
          .on("joined-meeting", () => {
            console.log("Joined meeting successfully");
            setIsInCall(true);
            setLoading(false);
          })
          .on("left-meeting", () => {
            console.log("Left meeting");
            setIsInCall(false);
            if (onLeave) onLeave();
          })
          .on("error", (error) => {
            console.error("Daily.co error:", error);
            setError(error?.errorMsg || "An error occurred with the video call");
            setLoading(false);
            onJoinError?.(error);
          });

        // Load call UI then join (avoid race where iframe isn't ready)
        await callObject.load({ url: roomUrl });
        await callObject.join({ userName });

        setLoading(false);
      } catch (err) {
        console.error("Error initializing call:", err);
        setError(err instanceof Error ? err.message : "Failed to initialize video call");
        setLoading(false);
      }
    };

    initializeCall();

    // Cleanup on unmount
    return () => {
      if (callObjectRef.current) {
        callObjectRef.current.destroy();
        callObjectRef.current = null;
      }
    };
  }, [roomUrl, userName, onLeave]);

  const leaveCall = () => {
    if (callObjectRef.current) {
      callObjectRef.current.leave();
    }
  };

  if (error) {
    return (
      <Card className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={() => window.location.reload()} className="mt-4">
          Retry
        </Button>
      </Card>
    );
  }

  return (
    <div className="relative w-full h-[calc(100vh-200px)] min-h-[600px]">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-10 rounded-lg">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Connecting to video call...</p>
          </div>
        </div>
      )}
      
      <div 
        ref={callContainerRef} 
        className="w-full h-full rounded-lg overflow-hidden bg-muted"
      />
    </div>
  );
};
